import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import Cookies from 'js-cookie';
import firebase from '../../Firebase';
import { useAuthContext } from '../../hooks/useAuthContext';
import NearestLocationForm from '../Location/NearestLocationField';
import DynamicFormMain from '../Profile&Settings/CommunityProfile/DynamicFormMain';
import ProfilePicturesGrid from '../Uploads/ProfilePhotoUpload';
import TypingIndicator from '../Message/TypingIndicator';
import ProfileSuggestions from '../Message/Suggestions';
import SuggestionProfile from '../Message/SuggestionProfile';
import Fanfare, { fanfareTheme } from './Fanfare';

const API = `${process.env.REACT_APP_API_BASE_URL}/aiOnboarding`;

// How long each sign-up celebration (Fanfare.js) plays before "You're in!"
// writes in — after the curtains, once the bikes are past.
const FANFARE_MS = { theatre: 3900, bikers: 1900, confetti: 700 };

// A bot line written in a few words at a time, each fading in — the same as
// the chat's other messages (AIOnboarding.css .aio-chunk). onDone once shown.
const CHUNK_WORDS = 3;
const CHUNK_MS = 70;
const ChunkText = ({ text, onDone }) => {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  useEffect(() => {
    const id = setTimeout(() => doneRef.current?.(), Math.ceil(words.length / CHUNK_WORDS) * CHUNK_MS + 300);
    return () => clearTimeout(id);
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <p className="demo-paragraph">
      {words.map((w, i) => (
        <React.Fragment key={i}>
          <span className="aio-chunk" style={{ animationDelay: `${Math.floor(i / CHUNK_WORDS) * CHUNK_MS}ms` }}>{w}</span>{i < words.length - 1 ? ' ' : ''}
        </React.Fragment>
      ))}
    </p>
  );
};


// Their first name, saved as they type (POST /userProfile/updateProfile, as
// the profile page does).
const NameField = ({ user, initial, onChange }) => {
  const [name, setName] = useState(initial || '');
  const timer = useRef(null);
  useEffect(() => { onChange(!!name.trim()); }, [name]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = (value) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      if (!value.trim() || !user?.userId) return;
      try {
        const idToken = await firebase.auth().currentUser?.getIdToken();
        await fetch(`${process.env.REACT_APP_API_BASE_URL}/userProfile/updateProfile`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}`, 'X-CSRF-Token': Cookies.get('mytoken') },
          body: JSON.stringify({ userId: user.userId, UserBasics: { firstName: value.trim().slice(0, 50) } }),
        });
      } catch { /* they can fix it on their Profile page */ }
    }, 600);
  };
  // Saved straight away if the chat already knows it.
  useEffect(() => { if (initial) save(initial); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <input
      className="aio-input"
      value={name}
      maxLength={50}
      placeholder="Your first name"
      autoComplete="given-name"
      onChange={(e) => { setName(e.target.value); save(e.target.value); }}
    />
  );
};

// After signing up in the AI chat, still in /meet: a moment of celebration,
// then the bot looks for people who might fit from what they told it
// (POST /aiOnboarding/suggest) and shows them — real suggestion cards, the
// same as in Messenger, tap one to see them and say if you're interested —
// or "no one yet, we'll keep you posted". Then the rest of their profile
// (name, area, photo, bio and any other written answers), already filled in
// with what they told the bot, so the best people can be suggested as they
// join. The conversation is saved to their bot chat in Messenger, which is
// where they carry on from next time.
const ChatOnboardingSteps = forwardRef(({ communityId, communityTitle, firstName, sessionId, communityPic, onFinished }, ref) => {
  const { user } = useAuthContext();
  // celebrate → (searching, only if the search is still running) → found → typing3 → form
  const [phase, setPhase] = useState('celebrate');
  // The search for people starts as soon as they're signed in; its answer
  // shows once "You're in!" has had its moment (introDone).
  const [result, setResult] = useState(null);
  const [introDone, setIntroDone] = useState(false);
  // "You're in!" waits for the celebration to finish; the cards wait for
  // "Here are some people…" to finish writing in.
  const theme = fanfareTheme(communityTitle);
  const [introStarted, setIntroStarted] = useState(false);
  const [cardsShown, setCardsShown] = useState(false);
  const sectionRef = useRef(null);
  // "Excellent! You're in!" is scrolled to the top of the chat, the
  // conversation before it out of view above. The section is at least as
  // tall as the chat, so there's room to.
  const youreInRef = useRef(null);
  const [paneHeight, setPaneHeight] = useState(0);
  const [found, setFound] = useState(null); // { text, suggestions, conversationId }
  const [openProfile, setOpenProfile] = useState(null);
  const [hasName, setHasName] = useState(!!firstName);
  const [locationInfo, setLocationInfo] = useState(null);
  const [photosInfo, setPhotosInfo] = useState(null);
  const [photosUploading, setPhotosUploading] = useState(false);
  const [writtenComplete, setWrittenComplete] = useState(false);
  const communityFormRef = useRef(null);
  // After the first suggestions: "Suggest more people", "Let me know when
  // better fits join", or anything they type — { role, text, suggestions }.
  const [extras, setExtras] = useState([]);
  // The first person they said they're interested in — the profile form
  // follows, to check whether that person would like to connect.
  const [interestedIn, setInterestedIn] = useState(null);
  const onInterest = (profile, isInterested) => {
    setOpenProfile(null);
    if (!isInterested || !profile) return;
    const name = profile.name || 'them';
    if (interestedIn) {
      setExtras(prev => [...prev, { role: 'bot', text: `Noted — I've let you know about ${name} too.` }]);
      return;
    }
    setInterestedIn(name);
    setPhase('typing3');
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }), 50);
    setTimeout(() => setPhase('form'), 300);
  };
  const [asking, setAsking] = useState(false);
  const endRef = useRef(null);
  const formRef = useRef(null);

  const askAfter = async (action, text) => {
    if (asking) return;
    setAsking(true);
    const label = text || (action === 'more' ? 'Suggest more people' : 'Let me know when better fits join');
    setExtras(prev => [...prev, { role: 'user', text: label }]);
    let reply = { text: "Sorry — I couldn't do that just now. Please try again in a moment.", suggestions: [] };
    try {
      const token = await firebase.auth().currentUser?.getIdToken();
      const res = await fetch(`${API}/after`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ sessionId, action, text }),
      });
      if (res.ok) reply = await res.json();
    } catch { /* keep the apology */ }
    setExtras(prev => [...prev, { role: 'bot', ...reply }]);
    setAsking(false);
  };
  useImperativeHandle(ref, () => ({ say: (text) => askAfter('say', text) }));
  useEffect(() => { if (extras.length) endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [extras.length]);
  // The profile form: scroll to its first line when it appears.
  useEffect(() => { if (phase === 'form') setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50); }, [phase]);

  // Look for people (once, from the moment they're signed in).
  useEffect(() => {
    if (!user) return undefined;
    let live = true;
    (async () => {
      let result = { text: "No one's a match just yet — but we'll keep you posted when people join. People are joining daily!", suggestions: [] };
      try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${API}/suggest`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ sessionId }),
        });
        if (res.ok) result = await res.json();
      } catch { /* keep the "no one yet" message */ }
      if (live) setResult(result);
    })();
    return () => { live = false; };
  }, [user, sessionId]);

  // Once signed in: the section takes the chat's full height, and the
  // celebration plays; "You're in!" starts when it's over.
  useEffect(() => {
    if (!user) return undefined;
    const pane = sectionRef.current?.closest('.demo-chatbox-messages');
    if (pane) setPaneHeight(pane.clientHeight);
    const id = setTimeout(() => setIntroStarted(true), FANFARE_MS[theme] ?? 700);
    return () => clearTimeout(id);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // "Excellent! You're in!" writes in, big, in the middle of the chat; holds
  // a moment; glides up to the top; then — after a brief beat — the people.
  useEffect(() => {
    if (!introStarted) return undefined;
    const el = youreInRef.current;
    const pane = el?.closest('.demo-chatbox-messages');
    if (!el || !pane) return undefined;
    setPaneHeight(pane.clientHeight);
    const offset = () => el.getBoundingClientRect().top - pane.getBoundingClientRect().top;
    const ids = [];
    requestAnimationFrame(() => {
      pane.scrollTo({ top: pane.scrollTop + offset() - (pane.clientHeight - el.offsetHeight) / 2 });
      ids.push(setTimeout(() => pane.scrollTo({ top: pane.scrollTop + offset() - 8, behavior: 'smooth' }), 1700));
      ids.push(setTimeout(() => setIntroDone(true), 1700 + 700 + 350));
    });
    return () => ids.forEach(clearTimeout);
  }, [introStarted]); // eslint-disable-line react-hooks/exhaustive-deps

  // Then the people (or "no one yet", straight on to the profile). If the
  // search is genuinely still running, "Let me have a look…" until it's back.
  useEffect(() => {
    if (!introDone || !['celebrate', 'searching'].includes(phase)) return undefined;
    if (!result) { setPhase('searching'); return undefined; }
    setFound(result);
    setPhase('found');
    return undefined;
  }, [introDone, result, phase]);

  // Once their line has written in: the cards — or, if nobody fits yet,
  // straight on to the profile. (With people, the profile comes once they
  // say they're interested in someone — onInterest.)
  const foundTextDone = () => {
    setCardsShown(true);
    if (!found?.suggestions?.length) setTimeout(() => setPhase((p) => (p === 'found' ? 'form' : p)), 300);
  };

  // "… bot is typing" only while really loading (their account, the search,
  // a reply) — not as a pause between messages.
  if (!user) return <TypingIndicator isBot communityName={communityTitle} />;

  const ready = hasName && !!locationInfo && writtenComplete && !photosUploading;
  const missing = [
    !hasName && 'your name',
    !locationInfo && 'your area',
    !writtenComplete && 'your bio',
  ].filter(Boolean);
  const after = () => ['found', 'typing3', 'form'].includes(phase);

  return (
    <div className="aio-after-signup" ref={sectionRef} style={paneHeight ? { minHeight: paneHeight } : undefined}>
      <Fanfare theme={theme} />
      {introStarted && (
      <div className="demo-message bot aio-youre-in-msg" ref={youreInRef}>
        <div className="demo-message-content">
          <div className="demo-message-text">
            {/* Written in a word at a time, like the bot's other messages. */}
            <p className="demo-paragraph aio-youre-in">
              {['Excellent!', 'You\u2019re', 'in!', '🎉'].map((w, i) => (
                <React.Fragment key={i}>
                  <span className="aio-chunk" style={{ animationDelay: `${150 + i * 140}ms` }}>{w}</span>{i < 3 ? ' ' : ''}
                </React.Fragment>
              ))}
            </p>
          </div>
        </div>
      </div>
      )}

      {phase === 'searching' && (
        <>
          <div className="demo-message bot">
            <div className="demo-message-content">
              <div className="demo-message-text"><p className="demo-paragraph">Let me have a look for people who might be a good fit…</p></div>
            </div>
          </div>
          <TypingIndicator isBot communityName={communityTitle} />
        </>
      )}

      {/* "Here are some people who could be a good fit:" writes in, then the cards. */}
      {after() && found && (
        <div className="demo-message bot">
          <div className="demo-message-content">
            <div className="demo-message-text"><ChunkText text={found.text} onDone={foundTextDone} /></div>
          </div>
        </div>
      )}
      {after() && found && cardsShown && found.suggestions?.length > 0 && (
        <div className="demo-message bot">
          <div className="demo-message-content aio-widget">
            <ProfileSuggestions
              userIds={found.suggestions}
              communityId={communityId}
              conversationId={found.conversationId}
              communityPic={communityPic}
              onSuggestionProfileClick={setOpenProfile}
              onRespond={onInterest}
            />
          </div>
        </div>
      )}
      {after() && found && cardsShown && !extras.length && <AfterButtons onAsk={askAfter} disabled={asking} />}

      {/* Asked for more, or typed something, after signing up. */}
      {extras.map((m, i) => (m.role === 'user' ? (
        <div key={i} className="demo-message user">
          <div className="demo-message-content">
            <div className="demo-message-text"><p className="demo-paragraph">{m.text}</p></div>
          </div>
        </div>
      ) : (
        <div key={i} className="demo-message bot">
          <div className="demo-message-content aio-widget">
            <p className="demo-paragraph">{m.text}</p>
            {m.suggestions?.length > 0 && (
              <ProfileSuggestions
                userIds={m.suggestions}
                communityId={communityId}
                conversationId={m.conversationId || found?.conversationId}
                communityPic={communityPic}
                onSuggestionProfileClick={setOpenProfile}
                onRespond={onInterest}
              />
            )}
          </div>
        </div>
      )))}
      {asking && <TypingIndicator isBot communityName={communityTitle} />}
      {extras.length > 0 && !asking && <AfterButtons onAsk={askAfter} disabled={asking} />}


      {phase === 'form' && (
        <div className="demo-message bot aio-step" ref={formRef}>
          <div className="demo-message-content aio-step-content">
            <p className="demo-paragraph aio-step-intro">
              {interestedIn
                ? <>Thanks for expressing interest in {interestedIn}! So I can check whether they&apos;d like to connect, I just need you to fill out the rest of your profile:</>
                : 'So we can suggest the best people when someone joins, would you like to fill in the rest of your profile now? See below:'}
            </p>

            <div className="aio-final-field">
              <div className="aio-final-label">{hasName ? '✓ ' : ''}Name</div>
              <NameField user={user} initial={firstName} onChange={setHasName} />
            </div>

            <div className="aio-final-field">
              <div className="aio-final-label">{locationInfo ? '✓ ' : ''}Location</div>
              <NearestLocationForm setLocationInfo={setLocationInfo} />
            </div>

            <div className="aio-final-field">
              <div className="aio-final-label">{photosInfo ? '✓ ' : ''}Profile pic</div>
              <ProfilePicturesGrid setPhotosInfo={setPhotosInfo} setPhotosUploading={setPhotosUploading} />
            </div>

            <div className="aio-final-field">
              <div className="aio-final-label">{writtenComplete ? '✓ ' : ''}About you</div>
              <DynamicFormMain
                ref={communityFormRef}
                user={user}
                communityId={communityId}
                onCommunityName={() => {}}
                setCommunityProfileComplete={setWrittenComplete}
                mode="Profile"
                onlyWrittenFields={true}
                autoSaveOnBlur={true}
              />
            </div>

            {!ready && missing.length > 0 && (
              <p className="aio-hint">Still needed: {missing.join(', ')}.</p>
            )}
            <button
              type="button"
              className="demo-signup-button aio-step-next aio-find-people"
              disabled={!ready}
              onClick={onFinished}
            >
              {photosUploading ? 'Uploading…' : 'Save and go to my chats'}
            </button>
          </div>
        </div>
      )}

      <div ref={endRef} />

      {/* A suggested person, tapped: their profile, to say interested or not. */}
      {openProfile && (
        <div className="aio-profile-overlay" role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) setOpenProfile(null); }}>
          <div className="aio-profile-sheet">
            <SuggestionProfile
              suggestionProfile={openProfile}
              onBackToChatBoxForCSS={() => setOpenProfile(null)}
              onProfileResponse={(userId, isInterested) => onInterest(openProfile, isInterested)}
              communityId={communityId}
              conversationId={found?.conversationId}
            />
          </div>
        </div>
      )}
    </div>
  );
});

// "Suggest more people" / "Let me know when better fits join", under the
// suggestions after signing up.
const AfterButtons = ({ onAsk, disabled }) => (
  <div className="aio-quick-replies">
    <button type="button" className="aio-quick-reply" disabled={disabled} onClick={() => onAsk('more')}>Suggest more people</button>
    <button type="button" className="aio-quick-reply" disabled={disabled} onClick={() => onAsk('notify')}>Let me know when better fits join</button>
  </div>
);

export default ChatOnboardingSteps;
